import { FC, useState } from "react";
import addressApi from "api/landlord/address";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Modal,
  Select
} from "shared/components";
import { XClose } from "untitledui-js-base";
import { states } from "utils/stateCodes";

import { getErrorMessage } from "utils/error";
import { usePlacesWidget } from "react-google-autocomplete";
import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { Property } from "types/landlord/address";
import { toast } from "react-toastify";

type Props = {
  opened: boolean;
  onClose: () => void;
  onPropertyCreated: (property: Property) => void;
};

export const CreatePropertyModal: FC<Props> = ({
  opened,
  onClose,
  onPropertyCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    address1: "",
    postal_code: "",
    city: "",
    state_or_province: "",
    country: ""
  });
  const [propertyName, setPropertyName] = useState<string>("");

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      const addressFields = googlePlaceToAddress(place);
      setAddress({
        address1: addressFields?.address1 || "",
        postal_code: addressFields?.postalCode,
        city: addressFields?.city,
        state_or_province: addressFields?.stateOrProvince,
        country: addressFields?.country
      });
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  const addProperty = async () => {
    setLoading(true);
    try {
      const property = await addressApi.addProperty(
        propertyName,
        {
          address1: address.address1,
          postal_code: address.postal_code,
          city: address.city,
          country: address.country,
          state_or_province: address.state_or_province
        },
        []
      );
      onPropertyCreated(property);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setLoading(false);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-[480px] flex flex-col p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <header className="text-xl font-bold text-content-1000">
          Create Property
        </header>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          borderLess
          Icon={XClose}
          onClick={onClose}
          className="focus:outline-none focus:ring-0"
        />
      </div>
      <form>
        <Input
          id="propertyName"
          label="Property Name"
          placeholder="Property Name"
          className="mb-4"
          required
          onChange={(e) => {
            setPropertyName(e.target.value);
          }}
        />
        <div>
          <Input
            id="selectedPlace"
            label="Street Address"
            required
            // @ts-expect-error
            ref={ref}
            className="mb-4"
            value={address.address1}
            onChange={(e) =>
              setAddress((prev) => ({ ...prev, address1: e.target.value }))
            }
          />
          <Input
            id="city"
            name="city"
            label="City"
            required
            className="mb-4"
            value={address.city}
            onChange={(e) =>
              setAddress((prev) => ({
                ...prev,
                [e.target.id]: e.target.value
              }))
            }
          />
          <div className="flex w-full gap-4">
            <div className="flex-1">
              <div className="flex w-full gap-4">
                <div className="flex-1">
                  <Input
                    id="postal_code"
                    name="postalCode"
                    label="Zip"
                    required
                    value={address.postal_code}
                    onChange={(e) =>
                      setAddress((prev) => ({
                        ...prev,
                        [e.target.id]: e.target.value
                      }))
                    }
                  />
                </div>
                <div className="mb-2 mt-auto h-fit w-full flex-1">
                  <Select
                    id="state_or_province"
                    label="State"
                    searchable
                    options={states}
                    value={address.state_or_province}
                    hideOptional
                    onChange={(e) =>
                      setAddress((prev) => ({
                        ...prev,
                        state_or_province: e.target.value
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-between gap-4 border-t border-alpha-40 pt-4">
          <div className="w-full">
            <Button onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              loading={loading}
              onClick={addProperty}
            >
              Confirm
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
